import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Plane, ShoppingCart, Inbox, Users, Truck, UserCog,
  Wallet, Menu, LogOut, Settings as SettingsIcon, Calculator as CalculatorIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Brand } from '@/components/Brand';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { roleLabel } from '@/lib/format';

const NAV = [
  { group: 'Operação', items: [
    { to: '/erp/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'supervisor', 'vendedor'] },
    { to: '/erp/viagens', label: 'Viagens', icon: Plane, roles: ['admin', 'supervisor', 'vendedor'] },
    { to: '/erp/calculadora', label: 'Calculadora', icon: CalculatorIcon, roles: ['admin', 'supervisor'] },
    { to: '/erp/vendas', label: 'Vendas', icon: ShoppingCart, roles: ['admin', 'supervisor', 'vendedor'] },
    { to: '/erp/leads', label: 'Leads', icon: Inbox, roles: ['admin', 'supervisor', 'vendedor'] },
  ]},
  { group: 'Cadastros', items: [
    { to: '/erp/clientes', label: 'Clientes', icon: Users, roles: ['admin', 'supervisor', 'vendedor'] },
    { to: '/erp/fornecedores', label: 'Fornecedores', icon: Truck, roles: ['admin', 'supervisor', 'vendedor'] },
    { to: '/erp/usuarios', label: 'Usuários', icon: UserCog, roles: ['admin', 'supervisor'] },
  ]},
  { group: 'Financeiro', items: [
    { to: '/erp/financeiro', label: 'Financeiro', icon: Wallet, roles: ['admin', 'supervisor'] },
    { to: '/erp/configuracoes', label: 'Configurações', icon: SettingsIcon, roles: ['admin', 'supervisor'] },
  ]},
];

function SidebarContent({ onNavigate }) {
  const location = useLocation();
  const { user } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <Link to="/" className="flex items-center gap-2 px-5 h-16 border-b border-border/60">
        <Brand />
      </Link>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {NAV.map((section) => {
          const items = section.items.filter((i) => i.roles.includes(user?.role));
          if (items.length === 0) return null;
          return (
            <div key={section.group}>
              <div className="px-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{section.group}</div>
              <div className="space-y-1">
                {items.map((item) => {
                  const active = location.pathname === item.to;
                  return (
                    <Link key={item.to} to={item.to} onClick={onNavigate}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${active ? 'bg-primary text-primary-foreground' : 'text-foreground/80 hover:bg-accent/60'}`}
                      data-testid={`erp-nav-${item.label.toLowerCase()}`}>
                      <item.icon className="h-4 w-4" /> {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export default function ErpLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const title = (() => {
    const p = location.pathname.split('/')[2] || 'dashboard';
    return ({ dashboard: 'Dashboard', viagens: 'Viagens', calculadora: 'Calculadora de Precificação', vendas: 'Vendas', leads: 'Leads',
      clientes: 'Clientes', fornecedores: 'Fornecedores', usuarios: 'Usuários', financeiro: 'Financeiro', configuracoes: 'Configurações' })[p] || 'Painel';
  })();

  const doLogout = async () => { await logout(); navigate('/login'); };
  const initials = (user?.name || 'U').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-muted/30">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] bg-card border-r border-border/60 flex-col">
        <SidebarContent />
      </aside>

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-30 h-16 bg-card/80 backdrop-blur border-b border-border/60 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="erp-mobile-menu"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[260px] p-0"><SidebarContent onNavigate={() => setOpen(false)} /></SheetContent>
            </Sheet>
            <h1 className="font-display text-xl font-semibold">{title}</h1>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full hover:bg-accent/60 p-1 pr-3" data-testid="erp-user-menu">
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-primary text-xs">{initials}</AvatarFallback></Avatar>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-[11px] text-muted-foreground">{roleLabel(user?.role)}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/')}>Ver site público</DropdownMenuItem>
              <DropdownMenuItem onClick={doLogout} className="text-destructive" data-testid="erp-logout"><LogOut className="h-4 w-4 mr-2" /> Sair</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 sm:p-6 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
